import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from 'recharts';
import {
  TrendingUp, ShieldCheck, Users, Building2, AlertTriangle
} from 'lucide-react';

const COLORS = ['#d97706', '#2563eb', '#059669', '#dc2626', '#7c3aed', '#0891b2', '#c026d3', '#ea580c'];
const STATUS_COLORS_MAP = {
  pending: '#f59e0b', in_progress: '#3b82f6', approved: '#10b981',
  partially_approved: '#14b8a6', rejected: '#ef4444', auto_rejected: '#e11d48',
};

export default function ExpenseAnalytics() {
  const { api, currentUser } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      const res = await api.getExpenseAnalytics({ user_id: currentUser.id, role: currentUser.role });
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, currentUser]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1,2,3].map(i => <Card key={i} className="border border-border/60 animate-pulse"><CardContent className="h-48" /></Card>)}
      </div>
    );
  }

  const complianceData = Object.entries(data.compliance || {}).map(([status, info]) => ({
    name: status.replace('_', ' '),
    value: info.count,
    fill: STATUS_COLORS_MAP[status] || '#94a3b8',
  }));

  return (
    <div className="space-y-4" data-testid="expense-analytics">
      {/* Row 1: Compliance + Spending Over Time */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Compliance Rate */}
        <Card className="border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" /> Policy Compliance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="relative w-24 h-24">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={complianceData} dataKey="value" innerRadius={28} outerRadius={42} paddingAngle={2}>
                      {complianceData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-slate-900">{data.compliance_rate}%</span>
                </div>
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-xs text-slate-500">{data.total_claims} total claims</p>
                {data.auto_rejected_count > 0 && (
                  <div className="flex items-center gap-1 text-xs text-rose-600">
                    <AlertTriangle className="w-3 h-3" />
                    {data.auto_rejected_count} auto-rejected
                  </div>
                )}
                <div className="flex flex-wrap gap-1 mt-1">
                  {complianceData.map((c, i) => (
                    <Badge key={i} className="text-[9px] py-0" style={{ backgroundColor: c.fill + '20', color: c.fill }}>
                      {c.name}: {c.value}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Spending Over Time */}
        <Card className="border border-border/60 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-amber-600" /> Spending Trend (Monthly)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.spending_over_time.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No data available yet</p>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={data.spending_over_time}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`Rs.${v.toLocaleString()}`, '']} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line type="monotone" dataKey="total" stroke="#d97706" strokeWidth={2} dot={{ r: 3 }} name="Claimed" />
                  <Line type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} name="Approved" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: By Business Line + By Role */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* By Business Line */}
        <Card className="border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-blue-600" /> By Business Line
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_business_line.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.by_business_line} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" width={90} />
                  <Tooltip formatter={(v) => [`Rs.${v.toLocaleString()}`, '']} contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="total" fill="#d97706" name="Claimed" radius={[0, 4, 4, 0]} barSize={16} />
                  <Bar dataKey="approved_amount" fill="#10b981" name="Approved" radius={[0, 4, 4, 0]} barSize={16} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* By Role */}
        <Card className="border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <Users className="w-4 h-4 text-violet-600" /> By Role
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.by_role.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-8">No data</p>
            ) : (
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={data.by_role}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 10 }} stroke="#94a3b8" tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => [`Rs.${v.toLocaleString()}`, '']} contentStyle={{ fontSize: 11 }} />
                  <Bar dataKey="total" fill="#7c3aed" name="Total Claimed" radius={[4, 4, 0, 0]} barSize={24}>
                    {data.by_role.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Budget Utilization + Top Spenders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Budget Utilization */}
        <Card className="border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700">Monthly Budget Utilization</CardTitle>
          </CardHeader>
          <CardContent>
            {data.budget_utilization.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No budget data this month</p>
            ) : (
              <div className="space-y-3">
                {data.budget_utilization.map((b, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="font-semibold text-slate-700">{b.role_label}</span>
                      <span className="text-slate-500">
                        {b.business_line_label} — Rs.{b.spent.toLocaleString()}
                        {b.limit > 0 && ` / Rs.${b.limit.toLocaleString()}`}
                      </span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          b.utilization > 100 ? 'bg-red-500' : b.utilization > 80 ? 'bg-amber-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${Math.min(b.utilization, 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[10px] text-slate-400">{b.count} claims</span>
                      <span className={`text-[10px] font-semibold ${
                        b.utilization > 100 ? 'text-red-600' : b.utilization > 80 ? 'text-amber-600' : 'text-emerald-600'
                      }`}>
                        {b.limit > 0 ? `${b.utilization}%` : 'No limit set'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Spenders */}
        <Card className="border border-border/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-bold text-slate-700">Top Spenders</CardTitle>
          </CardHeader>
          <CardContent>
            {data.top_spenders.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-6">No data</p>
            ) : (
              <div className="space-y-2">
                {data.top_spenders.map((s, i) => (
                  <div key={s.user_id} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      i === 0 ? 'bg-amber-100 text-amber-700' :
                      i === 1 ? 'bg-slate-100 text-slate-600' :
                      i === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-50 text-slate-400'
                    }`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-slate-900 truncate">{s.name}</p>
                      <p className="text-[10px] text-slate-400">{s.role_label} | {s.count} claims</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-slate-900">Rs.{s.total.toLocaleString()}</p>
                      {s.approved_total > 0 && <p className="text-[10px] text-emerald-600">Approved: Rs.{s.approved_total.toLocaleString()}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
